// src/pages/ProposalCreate.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWeb3 } from '../contexts/Web3Context';
import { useNotification } from '../contexts/NotificationContext';
import axios from 'axios';
import { PlusIcon, MinusIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import TokenSelectList from '../components/TokenSelectList';
import LoadingSpinner from '../components/LoadingSpinner';
import { XMarkIcon } from '@heroicons/react/24/outline';

const ProposalCreate = () => {
  const navigate = useNavigate();
  const { account, isOasysNetwork, connectWallet, switchToOasysNetwork, signMessage, isAdmin } =
    useWeb3();
  const { showNotification } = useNotification();

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [selectedToken, setSelectedToken] = useState(null);
  const [votingMethod, setVotingMethod] = useState('ONE_ADDRESS_ONE_VOTE');
  const [includeUnclaimed, setIncludeUnclaimed] = useState(false);
  const [startTime, setStartTime] = useState(new Date(Date.now() + 24 * 60 * 60 * 1000)); // 24時間後
  const [endTime, setEndTime] = useState(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)); // 1週間後
  const [choices, setChoices] = useState(['', '']);
  const [tokens, setTokens] = useState([]);
  const [isEligible, setIsEligible] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tokenRestriction, setTokenRestriction] = useState(false);

  // アカウント接続時に提案資格をチェック
  useEffect(() => {
    const checkEligibility = async () => {
      if (!account) {
        setIsEligible(false);
        setIsLoading(false);
        return;
      }

      try {
        // 管理者の場合は自動的に資格あり
        if (isAdmin) {
          setIsEligible(true);
          setIsLoading(false);
          return;
        }

        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/users/eligibility/${account}`
        );
        setIsEligible(response.data.eligible);
        setIsLoading(false);
      } catch (error) {
        console.error('資格チェックエラー:', error);
        setError('提案資格の確認中にエラーが発生しました');
        setIsEligible(false);
        setIsLoading(false);
      }
    };

    const fetchTokens = async () => {
      try {
        // システム設定を取得
        const configResponse = await axios.get(`${process.env.REACT_APP_API_URL}/api/config`);
        const config = configResponse.data;
        setTokenRestriction(config.proposalTokenRestriction?.enabled || false);

        // トークン制限が有効な場合の処理
        if (config.proposalTokenRestriction?.enabled) {
          // 管理者の場合は全トークンを取得可能
          if (isAdmin && config.proposalTokenRestriction.allowAdmin) {
            const allTokensResponse = await axios.get(
              `${process.env.REACT_APP_API_URL}/api/tokens`
            );
            // grantTokenがある有効なトークンのみフィルタリング
            const validTokens = allTokensResponse.data.filter(token => token.grantToken);
            setTokens(validTokens);
          } else {
            // 管理者以外または管理者でも許可されていない場合は自分のトークンのみ
            const myTokensResponse = await axios.get(
              `${process.env.REACT_APP_API_URL}/api/tokens/issuer/${account}`
            );
            // grantTokenがある有効なトークンのみフィルタリング
            const validTokens = myTokensResponse.data.filter(token => token.grantToken);
            setTokens(validTokens);
          }
        } else {
          // 制限なしの場合は全トークン
          const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/tokens`);
          // grantTokenがある有効なトークンのみフィルタリング
          const validTokens = response.data.filter(token => token.grantToken);
          setTokens(validTokens);
        }
      } catch (error) {
        console.error('トークン取得エラー:', error);
        setError('トークン情報の取得中にエラーが発生しました');

        // エラー時も全トークンをとりあえず表示
        try {
          const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/tokens`);
          // grantTokenがある有効なトークンのみフィルタリング
          const validTokens = response.data.filter(token => token.grantToken);
          setTokens(validTokens);
        } catch (e) {
          console.error('バックアップトークン取得エラー:', e);
        }
      }
    };

    if (account && isOasysNetwork) {
      checkEligibility();
      fetchTokens();
    } else {
      setIsLoading(false);
    }
  }, [account, isOasysNetwork, isAdmin]);

  // 選択肢を追加
  const addChoice = () => {
    setChoices([...choices, '']);
  };

  // 選択肢を削除
  const removeChoice = index => {
    if (choices.length <= 2) {
      showNotification('最低2つの選択肢が必要です', 'error');
      return;
    }
    const newChoices = [...choices];
    newChoices.splice(index, 1);
    setChoices(newChoices);
  };

  // 選択肢を更新
  const updateChoice = (index, value) => {
    const newChoices = [...choices];
    newChoices[index] = value;
    setChoices(newChoices);
  };

  // フォーム送信
  const handleSubmit = async e => {
    e.preventDefault();

    // バリデーション
    if (!account) {
      showNotification('ウォレットを接続してください', 'error');
      return;
    }

    if (!isOasysNetwork) {
      showNotification('OASYSネットワークに切り替えてください', 'error');
      return;
    }

    if (!title.trim()) {
      showNotification('タイトルを入力してください', 'error');
      return;
    }

    if (!description.trim()) {
      showNotification('提案内容を入力してください', 'error');
      return;
    }

    if (!selectedToken) {
      showNotification('トークンを選択してください', 'error');
      return;
    }

    // 選択肢のバリデーション
    const validChoices = choices.filter(choice => choice.trim() !== '');
    if (validChoices.length < 2) {
      showNotification('最低2つの有効な選択肢が必要です', 'error');
      return;
    }

    // 日時のバリデーション
    const now = new Date();
    if (startTime <= now) {
      showNotification('開始時間は現在より後に設定してください', 'error');
      return;
    }

    if (endTime <= startTime) {
      showNotification('終了時間は開始時間より後に設定してください', 'error');
      return;
    }

    try {
      setIsSubmitting(true);

      // 署名メッセージの作成
      const message = `提案を作成します\nタイトル: ${title}\nトークン: ${
        selectedToken.grantToken
      }\n${Date.now()}`;

      // 署名
      const signature = await signMessage(message);

      // 提案データの準備
      const proposalData = {
        title,
        description,
        tokenAddress: selectedToken.grantToken,
        startTime: startTime.toISOString(),
        endTime: endTime.toISOString(),
        choices: validChoices,
        votingMethod,
        includeUnclaimed,
        signature,
        message,
      };

      // APIリクエスト
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/proposals`,
        proposalData
      );

      showNotification('提案が作成されました', 'success');
      navigate(`/proposals/${response.data.id}`);
    } catch (error) {
      console.error('提案作成エラー:', error);
      showNotification(error.response?.data?.error || '提案の作成に失敗しました', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ウォレット接続ハンドラー
  const handleConnect = async () => {
    if (!account) {
      await connectWallet();
    } else if (!isOasysNetwork) {
      await switchToOasysNetwork();
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  // 接続されていない場合
  if (!account) {
    return (
      <div className="bg-white rounded-lg shadow p-8 max-w-2xl mx-auto text-center">
        <h1 className="text-2xl font-bold mb-4">提案を作成</h1>
        <p className="mb-6">提案を作成するには、まずウォレットを接続してください。</p>
        <button
          onClick={handleConnect}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition"
        >
          ウォレットを接続
        </button>
      </div>
    );
  }

  // OASYSネットワークに接続されていない場合
  if (!isOasysNetwork) {
    return (
      <div className="bg-white rounded-lg shadow p-8 max-w-2xl mx-auto text-center">
        <h1 className="text-2xl font-bold mb-4">提案を作成</h1>
        <p className="mb-6">提案を作成するには、OASYSネットワークに切り替えてください。</p>
        <button
          onClick={switchToOasysNetwork}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition"
        >
          OASYSネットワークに切り替え
        </button>
      </div>
    );
  }

  // 提案資格がない場合
  if (!isEligible && !isAdmin) {
    // 管理者でなく、かつ資格がない場合
    return (
      <div className="bg-white rounded-lg shadow p-8 max-w-2xl mx-auto">
        <h1 className="text-2xl font-bold mb-4 text-center">提案資格がありません</h1>
        {/* <p className="mb-6">
          提案を作成するには、以下のいずれかの条件を満たす必要があります：
          <ul className="list-disc pl-6 mt-2">
            <li>yukichi.funでトークンを発行したアドレスであること</li>
            <li>10,000 LIDERU以上のトークンを保有していること</li>
            <li>管理者アドレスとして登録されていること</li>
          </ul>
        </p> */}
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-8 max-w-2xl mx-auto">
      <h1 className="text-2xl font-bold mb-6 text-center text-gray-900 dark:text-white">
        新しい提案を作成
      </h1>

      {error && (
        <div className="bg-red-100 dark:bg-red-900 border border-red-400 dark:border-red-700 text-red-700 dark:text-red-200 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {/* タイトル */}
        <div className="mb-4">
          <label className="block text-gray-700 dark:text-gray-300 text-sm font-bold mb-2">
            タイトル <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={title}
            onChange={e => setTitle(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            placeholder="提案のタイトル"
            required
          />
        </div>

        {/* 説明 → 提案内容に変更 */}
        <div className="mb-4">
          <label className="block text-gray-700 dark:text-gray-300 text-sm font-bold mb-2">
            提案内容 <span className="text-red-500">*</span>
          </label>
          <textarea
            value={description}
            onChange={e => setDescription(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            placeholder="提案の詳細な内容"
            rows="5"
            required
          />
        </div>

        <div className="mb-4">
          <label className="block text-gray-700 dark:text-gray-300 text-sm font-bold mb-2">
            投票権コイン <span className="text-red-500">*</span>
          </label>

          {tokenRestriction && (
            <div className="flex items-start mb-2 p-3 bg-yellow-50 dark:bg-yellow-900/30 rounded-md border border-yellow-200 dark:border-yellow-800">
              <InformationCircleIcon className="h-5 w-5 text-yellow-600 dark:text-yellow-500 flex-shrink-0 mr-2 mt-0.5" />
              <p className="text-sm text-yellow-700 dark:text-yellow-400">
                自分が発行したコインのみ選択可能です
              </p>
            </div>
          )}

          {/* 選択されたコインを表示 */}
          {selectedToken && (
            <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md">
              <div className="flex items-center">
                <div className="flex items-center justify-center w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-800 text-blue-700 dark:text-blue-300 font-bold mr-3">
                  {/* {selectedToken.symbol ? selectedToken.symbol.substring(0, 2).toUpperCase() : 'TK'} */}
                  <img
                    src={selectedToken.img}
                    alt={selectedToken.name}
                    className="w-12 h-12 rounded-full object-cover mr-3"
                    onError={e => {
                      // 画像が読み込めない場合はフォールバック
                      e.target.style.display = 'none';
                      e.target.nextElementSibling.style.display = 'flex';
                    }}
                  />
                </div>
                <div className="flex-1">
                  <div className="font-medium text-blue-800 dark:text-blue-300">
                    {selectedToken.name}
                  </div>
                  <div className="text-sm text-blue-600 dark:text-blue-400 flex items-center">
                    <span>{selectedToken.symbol}</span>
                    {selectedToken.pairType && (
                      <span className="ml-2 px-2 py-0.5 bg-blue-100 dark:bg-blue-800 rounded-full text-xs">
                        {selectedToken.pairType}
                      </span>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setSelectedToken(null)}
                  className="text-blue-500 hover:text-blue-700 dark:hover:text-blue-300"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
          )}

          <TokenSelectList
            tokens={tokens}
            // selectedToken={selectedToken}
            onSelectToken={setSelectedToken}
          />
        </div>

        {/* 投票期間 */}
        <div className="mb-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-gray-700 dark:text-gray-300 text-sm font-bold mb-2">
              開始日時 <span className="text-red-500">*</span>
            </label>
            <DatePicker
              selected={startTime}
              onChange={setStartTime}
              showTimeSelect
              timeFormat="HH:mm"
              timeIntervals={15}
              dateFormat="yyyy/MM/dd HH:mm"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              minDate={new Date()}
            />
          </div>

          <div>
            <label className="block text-gray-700 dark:text-gray-300 text-sm font-bold mb-2">
              終了日時 <span className="text-red-500">*</span>
            </label>
            <DatePicker
              selected={endTime}
              onChange={setEndTime}
              showTimeSelect
              timeFormat="HH:mm"
              timeIntervals={15}
              dateFormat="yyyy/MM/dd HH:mm"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              minDate={startTime}
            />
          </div>
        </div>

        {/* 投票方式 */}
        <div className="mb-4">
          <label className="block text-gray-700 dark:text-gray-300 text-sm font-bold mb-2">
            投票方式
          </label>
          <div className="flex gap-4">
            <label className="inline-flex items-center">
              <input
                type="radio"
                value="ONE_ADDRESS_ONE_VOTE"
                checked={votingMethod === 'ONE_ADDRESS_ONE_VOTE'}
                onChange={() => setVotingMethod('ONE_ADDRESS_ONE_VOTE')}
                className="form-radio h-4 w-4 text-blue-600"
              />
              <span className="ml-2 text-gray-700 dark:text-gray-300">1アドレス1票</span>
            </label>

            <label className="inline-flex items-center">
              <input
                type="radio"
                value="TOKEN_WEIGHTED"
                checked={votingMethod === 'TOKEN_WEIGHTED'}
                onChange={() => setVotingMethod('TOKEN_WEIGHTED')}
                className="form-radio h-4 w-4 text-blue-600"
              />
              <span className="ml-2 text-gray-700 dark:text-gray-300">コイン量比例</span>
            </label>
          </div>
        </div>

        {/* 未Claim対応 */}
        <div className="mb-6">
          <label className="inline-flex items-center">
            <input
              type="checkbox"
              checked={includeUnclaimed}
              onChange={e => setIncludeUnclaimed(e.target.checked)}
              className="form-checkbox h-4 w-4 text-blue-600"
            />
            <span className="ml-2 text-gray-700 dark:text-gray-300">
              未Claimのコインも投票権に含める
            </span>
          </label>
        </div>

        {/* 選択肢 - "選択肢を追加"ボタンを横に配置 */}
        <div className="mb-6">
          <div className="flex justify-between items-center mb-2">
            <label className="block text-gray-700 dark:text-gray-300 text-sm font-bold">
              選択肢 <span className="text-red-500">*</span>
            </label>
            <button
              type="button"
              onClick={addChoice}
              className="flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 text-sm"
            >
              <PlusIcon className="h-4 w-4 mr-1" />
              <span>選択肢を追加</span>
            </button>
          </div>

          {choices.map((choice, index) => (
            <div key={index} className="flex items-center mb-2">
              <input
                type="text"
                value={choice}
                onChange={e => updateChoice(index, e.target.value)}
                className="flex-grow px-3 py-2 border border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                placeholder={`選択肢 ${index + 1}`}
                required
              />

              <button
                type="button"
                onClick={() => removeChoice(index)}
                className="ml-2 p-2 text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300"
                disabled={choices.length <= 2}
              >
                <MinusIcon className="h-5 w-5" />
              </button>
            </div>
          ))}
        </div>

        {/* 送信ボタン */}
        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => navigate('/proposals')}
            className="px-4 py-2 mr-2 border border-gray-300 dark:border-gray-600 rounded text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition"
            disabled={isSubmitting}
          >
            キャンセル
          </button>

          <button
            type="submit"
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition disabled:bg-blue-300 dark:disabled:bg-blue-800"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <span className="flex items-center">
                <LoadingSpinner size="sm" />
                <span className="ml-2">送信中...</span>
              </span>
            ) : (
              '提案を作成'
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ProposalCreate;
